import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { EASE } from "./Reveal";
import { MaskedLine } from "./MaskedLine";
import { AviahMark } from "./AviahMark";

// Monumental editorial timeline: capabilities built over time, converging into
// Aviah Financial. Champagne progress line, alternating stages, masked words.
const FounderTimeline = ({ stages, finale }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.7", "end 0.6"],
  });
  const progress = useSpring(scrollYProgress, { stiffness: 55, damping: 18 });

  return (
    <div ref={ref} data-testid="founder-timeline">
      <div className="relative">
        <div className="absolute bottom-0 left-[7px] top-0 w-px bg-[#171717]/15 sm:left-1/2" />
        <motion.div
          className="absolute bottom-0 left-[7px] top-0 w-px origin-top bg-[#C6A46A] sm:left-1/2"
          style={{ scaleY: progress }}
        />

        <div className="space-y-24 sm:space-y-32">
          {stages.map((s, i) => {
            const leftSide = i % 2 === 0;
            return (
              <div
                key={i}
                data-testid={`timeline-stage-${i + 1}`}
                className={`relative pl-12 sm:w-1/2 sm:pl-0 ${
                  leftSide ? "sm:pr-16 sm:text-right" : "sm:ml-auto sm:pl-16"
                }`}
              >
                <motion.span
                  initial={{ scale: 0.3, opacity: 0.25 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, margin: "-90px" }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className={`absolute top-2.5 block h-[13px] w-[13px] rounded-full border border-[#C6A46A] bg-[#C6A46A] ${
                    leftSide ? "left-[1px] sm:-right-[6.5px] sm:left-auto" : "left-[1px] sm:-left-[6.5px]"
                  }`}
                />
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: "-90px" }}
                  transition={{ duration: 0.5 }}
                  className={`flex items-baseline gap-4 ${leftSide ? "sm:justify-end" : ""}`}
                >
                  <span className="text-sm font-light tracking-[0.25em] text-[#C6A46A]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.micro && (
                    <span className="text-[0.6rem] uppercase tracking-[0.22em] text-[#171717]/40">{s.micro}</span>
                  )}
                </motion.div>
                <h3 className="mt-4 text-3xl font-light uppercase leading-[1.06] tracking-tight text-[#171717] sm:text-4xl lg:text-[2.75rem]">
                  {s.lines.map((l, j) => (
                    <MaskedLine key={j} delay={j * 0.12}>
                      {l}
                    </MaskedLine>
                  ))}
                </h3>
                <motion.p
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-70px" }}
                  transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
                  className="mt-5 max-w-md text-sm leading-relaxed text-[#171717]/60 sm:inline-block"
                >
                  {s.d}
                </motion.p>
              </div>
            );
          })}
        </div>
      </div>

      {/* The convergence: every stage resolves into Aviah Financial. */}
      <div
        className="relative mt-28 overflow-hidden border border-[#C6A46A]/40 bg-[#F4F0E8] px-6 py-20 text-center sm:mt-36 sm:py-28"
        data-testid="timeline-finale"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.07 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.4 }}
          className="pointer-events-none absolute left-1/2 top-1/2 h-[30rem] w-[30rem] -translate-x-1/2 -translate-y-1/2"
          aria-hidden="true"
        >
          <AviahMark className="h-full w-full text-[#C6A46A]" strokeWidth={1.6} />
        </motion.div>

        <svg viewBox="0 0 120 100" className="relative mx-auto h-16 w-20" fill="none" aria-hidden="true">
          <motion.path
            d="M 92 22 A 45 45 0 1 0 92 78"
            stroke="#C6A46A"
            strokeWidth="2.2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
          />
          <motion.line
            x1="66" y1="94" x2="90" y2="6"
            stroke="#C6A46A"
            strokeWidth="2.2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, delay: 0.7, ease: "easeInOut" }}
          />
          <motion.line
            x1="82" y1="94" x2="106" y2="6"
            stroke="#C6A46A"
            strokeWidth="2.2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, delay: 0.85, ease: "easeInOut" }}
          />
        </svg>

        <h3 className="relative mt-10 text-3xl font-light leading-[1.12] tracking-tight text-[#171717] sm:text-4xl lg:text-5xl">
          {finale.lines.map((l, i) => (
            <MaskedLine key={i} delay={i * 0.12}>
              {l}
            </MaskedLine>
          ))}
        </h3>

        <motion.img
          src="/assets/logo-black.webp"
          alt="Aviah Financial"
          className="relative mx-auto mt-10 w-60 sm:w-72"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
        />

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="relative mt-8 text-[0.65rem] font-light uppercase tracking-[0.3em] text-[#C6A46A]"
        >
          {finale.tagline.join("\u00A0\u00A0•\u00A0\u00A0")}
        </motion.p>
      </div>
    </div>
  );
};

export default FounderTimeline;
